import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, FlatList, StyleSheet, Alert, ActivityIndicator, RefreshControl, Animated } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import AppShell from '../../components/layout/AppShell';
import ScreenCommandHeader from '../../components/layout/ScreenCommandHeader';
import TopBar from '../../components/layout/TopBar';
import ClubCard from '../../components/clubs/ClubCard';
import EmptyState from '../../components/work/EmptyState';
import FloatingActionButton from '../../components/common/FloatingActionButton';
import { ClubsStackParamList } from '../../types/navigation';
import { ClubView } from '../../types/clubs';
import { getClubs, Club, joinClub } from '../../lib/api/clubs';
import { useAuth } from '../../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

type ClubsHomeNavigationProp = StackNavigationProp<ClubsStackParamList, 'ClubsHome'>;

const ClubsHomeScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<ClubsHomeNavigationProp>();
  const { user } = useAuth();
  const [selectedView, setSelectedView] = useState<ClubView>('my-clubs');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  
  const headerTranslate = useMemo(() => {
    if (headerHeight === 0) {
      return 0;
    }
    return scrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, headerHeight]);

  useEffect(() => {
    loadClubs();
  }, []);

  // Refresh clubs when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadClubs(true);
    }, [])
  );

  const loadClubs = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await getClubs();
      setClubs(data);
    } catch (error) {
      console.error('Error loading clubs:', error);
      Alert.alert('Error', 'Failed to load clubs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    loadClubs(true);
  };

  const myClubs = clubs.filter(club => club.membershipStatus === 'joined' || club.membershipStatus === 'pending');
  
  // Filter discover clubs: admins see all, regular users only see clubs in their city
  const discoverClubs = user?.role === 'admin' 
    ? clubs 
    : clubs.filter(club => club.city === user?.city);

  const handleClubPress = (clubId: string) => {
    navigation.navigate('ClubDetail', { clubId });
  };

  const handleChatPress = (clubId: string) => {
    navigation.navigate('ClubChat', { clubId });
  };

  const handleCreateClubPress = () => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      Alert.alert('Access Denied', 'Only admins can create clubs');
      return;
    }
    navigation.navigate('CreateClub');
  };

  const handleJoinClub = async (clubId: string, clubName: string) => {
    // If admin, join directly
    if (user?.role === 'admin') {
      try {
        await joinClub(clubId, {
          phone: user?.phoneNumber || '',
          profession: user?.profession || '',
        });
        showSuccessToast(`You have joined ${clubName}!`);
        // Reload clubs to update the list
        loadClubs();
      } catch (error: any) {
        showErrorToast(error.message || 'Failed to join club');
      }
      return;
    }

    // Navigate to join form for non-admins
    navigation.navigate('JoinClubForm', { clubId, clubName });
  };

  if (loading) {
    return (
      <AppShell showTopBar={false} scrollable={false}>
        <View style={[styles.container, { backgroundColor: theme.background }]}>
          <Animated.View
            style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
            onLayout={(event) => {
              const height = event.nativeEvent.layout.height;
              if (height !== headerHeight) {
                setHeaderHeight(height);
              }
            }}
          >
            <TopBar title="Clubs" showLogo={false} />
            <ScreenCommandHeader
              segments={[
                { value: 'my-clubs' as const, label: 'My Circles' },
                { value: 'discover' as const, label: 'Discover' },
              ]}
              selectedValue={selectedView}
              onValueChange={setSelectedView}
            />
          </Animated.View>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.accent} />
          </View>
        </View>
      </AppShell>
    );
  }

  const renderMyClubs = () => {
    if (myClubs.length === 0) {
      return <EmptyState icon="people-outline" message="You haven't joined any circles yet" />;
    }

    return (
      <Animated.FlatList
        data={myClubs}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <ClubCard
            club={item}
            onPress={() => handleClubPress(item.id)}
            status={item.membershipStatus || 'not-member'}
            onChatPress={() => handleChatPress(item.id)}
          />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.accent}
            colors={[theme.accent]}
          />
        }
      />
    );
  };

  const renderDiscoverClubs = () => {
    if (discoverClubs.length === 0) {
      return <EmptyState icon="search-outline" message="No circles found" />;
    }

    return (
      <Animated.FlatList
        data={discoverClubs}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <ClubCard
            club={item}
            onPress={() => handleClubPress(item.id)}
            status={item.membershipStatus || 'not-member'}
            showJoinButton={item.membershipStatus === 'not-member'}
            onJoinPress={() => handleJoinClub(item.id, item.name)}
            onChatPress={() => handleChatPress(item.id)}
          />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.accent}
            colors={[theme.accent]}
          />
        }
      />
    );
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'my-clubs':
        return renderMyClubs();
      case 'discover':
        return renderDiscoverClubs();
      default:
        return renderMyClubs();
    }
  };

  return (
    <AppShell title="Clubs" scrollable={false} showTopBar={false}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          <TopBar title="Clubs" showLogo={false} />
          <ScreenCommandHeader
            segments={[
              { value: 'my-clubs' as const, label: 'My Circles' },
              { value: 'discover' as const, label: 'Discover' },
            ]}
            selectedValue={selectedView}
            onValueChange={setSelectedView}
          />
        </Animated.View>
        <View style={styles.content}>
          {renderContent()}
        </View>
        {user?.role === 'admin' && (
          <FloatingActionButton onPress={handleCreateClubPress} />
        )}
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  content: {
    flex: 1,
  },
  listContent: {
    paddingTop: 8,
    paddingBottom: 80,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ClubsHomeScreen;
